<template>
  <b-modal
    id="product-reject-details-modal"
    v-model="syncVisible"
    modal-class="product-reject-details-modal"
    content-class="product-reject-details-modal__content border-0"
    body-class="product-reject-details-modal__body"
    size="lg"
    centered
    hide-header
    hide-footer
    @hidden="$emit('input', false)"
  >
    <button
      type="button"
      class="product-reject-details-modal__close"
      :aria-label="$t('lbl_EMPBE_Cancel')"
      @click="close"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6L18 18M18 6L6 18" stroke="rgba(0,0,0,0.6)" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>

    <div class="product-reject-details-modal__header">
      <h2 class="product-reject-details-modal__title">
        {{ $t('lbl_EMPBE_ProductInfo_RejectDetails_Title', { totalNo: items.length }) }}
      </h2>
      <p class="product-reject-details-modal__desc">
        {{ $t('lbl_EMPBE_ProductInfo_RejectDetails_Desc') }}
      </p>
      <p v-if="rejectedTimeText" class="product-reject-details-modal__time">
        {{ $t('lbl_EMPBE_ProductInfo_RejectDetails_RejectedTime', { time: rejectedTimeText }) }}
      </p>
    </div>

    <div class="product-reject-details-modal__list">
      <div
        v-for="(item, index) in items"
        :key="item.key"
        class="product-reject-details-modal__card"
      >
        <div class="product-reject-details-modal__path">
          <strong class="product-reject-details-modal__path-index">#{{ index + 1 }}</strong>
          <span class="product-reject-details-modal__path-section">{{ item.sectionLabel }}</span>
          <i class="fa fa-angle-right product-reject-details-modal__path-sep" aria-hidden="true"></i>
          <strong class="product-reject-details-modal__path-field">{{ item.fieldLabel }}</strong>
        </div>
        <div v-if="item.contentText" class="product-reject-details-modal__content-line">
          <template v-if="item.kind === 'file'">
            <span class="product-reject-details-modal__content-label">{{ $t('lbl_EMPBE_ProductInfo_RejectDetails_Files') }}:</span>
            <span class="product-reject-details-modal__content-value">{{ item.contentText }}</span>
          </template>
          <template v-else>
            <span class="product-reject-details-modal__content-label">{{ $t('lbl_EMPBE_ProductInfo_RejectDetails_Content') }}:</span>
            <span class="product-reject-details-modal__content-value">{{ item.contentText }}</span>
          </template>
        </div>
        <div class="product-reject-details-modal__reason">
          <!-- <span class="product-reject-details-modal__reason-icon" aria-hidden="true">!</span> -->
          <span class="product-reject-details-modal__reason-label">{{ $t('lbl_EMPBE_ProductInfo_RejectDetails_RejectedLabel') }}</span>
          <span class="product-reject-details-modal__reason-text">{{ item.rejectReason }}</span>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  resolveProductApprovalContentKind,
  parseProductApprovalContentDisplay,
  resolveProductApprovalSectionLabel,
  resolveProductApprovalFieldLabel,
  resolveProductRejectReasonText,
} from './productApprovalFieldMeta';
import { isVisibleApprovalRejectRow } from '@/constants/approvalRejectUi.js';

export default {
  name: 'ProductRejectDetailsModal',
  props: {
    value: { type: Boolean, default: false },
    results: { type: Array, default: () => [] },
    rejectedTime: { default: null },
    isApprovalMode: { type: Boolean, default: false },
  },
  computed: {
    syncVisible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', !!v);
      },
    },
    items() {
      const host = { isApprovalMode: this.isApprovalMode };
      const rows = (this.results || []).filter((row) =>
        isVisibleApprovalRejectRow(row, host),
      );
      return rows.map((row, idx) => {
        const usageType = String(row.usageType || '').trim();
        const kind = resolveProductApprovalContentKind(row);
        const sectionLabel = resolveProductApprovalSectionLabel(
          this.$t.bind(this),
          row.tabSection,
          this.$t('lbl_EMPBE_ProductInfo_Title'),
        );
        const fieldLabel = resolveProductApprovalFieldLabel(
          this.$t.bind(this),
          usageType,
          row.fieldName,
        );
        const contentText = parseProductApprovalContentDisplay(row.content, kind);
        return {
          key: `${row.approvalRequestResultId || row.approvalRequestContentId || usageType}_${idx}`,
          sectionLabel,
          fieldLabel,
          kind,
          contentText,
          rejectReason: resolveProductRejectReasonText(row.rejectReason),
        };
      });
    },
    rejectedTimeText() {
      if (!this.rejectedTime) return '';
      const d = this.rejectedTime instanceof Date
        ? this.rejectedTime
        : new Date(this.rejectedTime);
      if (Number.isNaN(d.getTime())) return String(this.rejectedTime);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day} ${hh}:${mm}`;
    },
  },
  methods: {
    close() {
      this.syncVisible = false;
    },
  },
};
</script>

<style lang="scss">
.product-reject-details-modal .modal-dialog {
  max-width: 800px;
}

.product-reject-details-modal__content {
  border-radius: 12px;
  overflow: hidden;
}

.product-reject-details-modal__body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px !important;
  max-height: min(80vh, 760px);
  overflow: hidden;
}

.product-reject-details-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-reject-details-modal__header {
  flex-shrink: 0;
  padding-right: 40px;
  margin-bottom: 0;
}

.product-reject-details-modal__title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.85);
}

.product-reject-details-modal__desc {
  margin: 0 0 4px;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.65);
}

.product-reject-details-modal__contact {
  color: #2c6ecb;
  text-decoration: none;
  margin: 0 2px;
}

.product-reject-details-modal__contact:hover {
  text-decoration: underline;
}

.product-reject-details-modal__time {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.45);
}

.product-reject-details-modal__list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding-right: 4px;
}

.product-reject-details-modal__card {
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
}

.product-reject-details-modal__path {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 8px;
}

.product-reject-details-modal__path-index {
  flex-shrink: 0;
  font-weight: 700;
}

.product-reject-details-modal__path-section {
  min-width: 0;
}

.product-reject-details-modal__path-sep {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.45);
}

.product-reject-details-modal__path-field {
  min-width: 0;
  font-weight: 700;
}

.product-reject-details-modal__content-line {
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 10px;
  word-break: break-word;
}

.product-reject-details-modal__content-label {
  color: rgba(0, 0, 0, 0.60);
  margin-right: 4px;
}

.product-reject-details-modal__content-value {
  color: rgba(0, 0, 0, 0.80);
}

.product-reject-details-modal__reason {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(205, 21, 21, 0.06);
  font-size: 14px;
  line-height: 1.4;
}

.product-reject-details-modal__reason-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border-radius: 50%;
  background: #cd1515;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.product-reject-details-modal__reason-label {
  flex-shrink: 0;
  font-weight: 700;
  color: #cd1515;
}

.product-reject-details-modal__reason-text {
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(0, 0, 0, 0.8);
}
</style>
